/**
 * Le statut d'une inscription, du point de vue de la famille.
 *
 * ## Il vient de l'inscription, pas du paiement
 *
 * Les écrans des parents affichaient jusqu'ici `payment_status` : « Payé » ou
 * « En attente ». Deux raisons de cesser.
 *
 * La première est que ce n'était pas vrai. L'architecture des paiements a
 * changé — une facture est acquittée quand elle est rapprochée d'une écriture
 * bancaire, et `registrations.payment_status` n'a pas suivi. Sur les 3 715
 * factures de la base, 2 256 réglées s'affichaient « En attente » et 28
 * impayées s'affichaient « Payé » : 63 % des lignes disaient le contraire de
 * la réalité. Une famille à jour qui lit « En attente » écrit au secrétariat ;
 * une famille en retard qui lit « Payé » n'écrit à personne.
 *
 * La seconde est que ce n'est pas la question. Un parent qui ouvre « mes
 * inscriptions » cherche à savoir si son enfant a sa place cette semaine-là.
 * Où en est la comptabilité est une autre question, elle se pose ailleurs, et
 * la mêler à celle-ci rend les deux illisibles.
 *
 * Restent donc trois états, tous lus sur l'inscription elle-même :
 *
 *   - **annulée**  — le stage n'aura pas lieu pour cet enfant ;
 *   - **en attente** — une annulation est demandée, pas encore traitée ;
 *   - **validée**  — la place est tenue.
 */

/** Ce que porte la colonne `registrations.cancellation_status`. */
export type StatutAnnulation =
  | 'none'
  | 'requested'
  | 'cancelled_full_refund'
  | 'cancelled_partial_refund'
  | 'cancelled_no_refund';

export type Statut = 'validee' | 'demande' | 'annulee';

export interface StatutAffiche {
  cle: Statut;
  libelle: string;
  /** Une phrase de plus, quand l'état seul laisserait le parent dans le doute. */
  detail?: string;
  /** La couleur, dite une fois : rouge pour l'annulation, ambre pour l'attente. */
  ton: 'vert' | 'ambre' | 'rouge';
}

/**
 * Le statut d'une inscription.
 *
 * Une valeur inconnue devient « validée » — et c'est le choix prudent : la
 * famille a réservé, et rien dans la base ne dit le contraire. Afficher
 * « annulée » sur un doute ferait croire à une place perdue qui ne l'est pas.
 */
export function statutInscription(annulation: StatutAnnulation | string | null | undefined): StatutAffiche {
  switch (annulation) {
    case 'cancelled_full_refund':
    case 'cancelled_partial_refund':
    case 'cancelled_no_refund':
      return { cle: 'annulee', libelle: 'Annulée', ton: 'rouge' };
    case 'requested':
      return {
        cle: 'demande',
        libelle: 'Annulation demandée',
        detail: 'Nous traitons votre demande ; la place reste tenue entre-temps.',
        ton: 'ambre',
      };
    default:
      return { cle: 'validee', libelle: 'Inscription validée', ton: 'vert' };
  }
}

/** Vrai si l'inscription tient encore une place. */
export function estActive(annulation: StatutAnnulation | string | null | undefined): boolean {
  return statutInscription(annulation).cle !== 'annulee';
}

/**
 * Vrai si l'annulation peut encore être demandée.
 *
 * On ne propose pas d'annuler ce qui est déjà annulé, ni ce dont l'annulation
 * est en cours de traitement — un second bouton créerait une seconde demande,
 * et le secrétariat en recevrait deux pour la même semaine.
 */
export function peutDemanderAnnulation(annulation: StatutAnnulation | string | null | undefined): boolean {
  return statutInscription(annulation).cle === 'validee';
}

/**
 * « du 7 au 11 juillet », ou « le 7 juillet » quand c'est un seul jour.
 *
 * L'année n'apparaît que si ce n'est pas l'année en cours. Elle est inutile
 * neuf fois sur dix — mais un parent qui lit « du 22 au 24 décembre » en
 * janvier a besoin de savoir s'il s'agit du mois prochain ou de l'an dernier.
 */
export function periodeLisible(debut: string, fin: string, maintenant = new Date()): string {
  const d = new Date(debut);
  const f = new Date(fin);
  if (Number.isNaN(d.getTime()) || Number.isNaN(f.getTime())) return '';

  const annee = f.getFullYear() === maintenant.getFullYear() ? '' : ` ${f.getFullYear()}`;
  const jour = (x: Date) => x.toLocaleDateString('fr-BE', { day: 'numeric', month: 'long' });
  if (d.toDateString() === f.toDateString()) return `le ${jour(d)}${annee}`;

  /*
    Le mois n'est répété que s'il change : « du 7 au 11 juillet » se lit mieux
    que « du 7 juillet au 11 juillet », et c'est la forme de loin la plus
    fréquente puisqu'un stage dure une semaine.
  */
  if (d.getMonth() === f.getMonth() && d.getFullYear() === f.getFullYear()) {
    return `du ${d.toLocaleDateString('fr-BE', { day: 'numeric' })} au ${jour(f)}${annee}`;
  }
  return `du ${jour(d)} au ${jour(f)}${annee}`;
}

/** Vrai si la semaine est passée : l'annulation n'a plus d'objet. */
export function estPassee(fin: string, maintenant = new Date()): boolean {
  const f = new Date(fin);
  if (Number.isNaN(f.getTime())) return false;
  return f.getTime() < maintenant.getTime();
}
